import ENDPOINTS from 'api/endpoints';
import {
  buildApiHeaders,
  buildApiUrl,
  doDelete,
  doGet,
  doPatch,
  doPost,
  processSSEStream,
} from 'api/request';
import type {
  AnalysisTemplatesResponse,
  SmartAnalystChatMessageReview,
  SmartAnalystChatMetadata,
  SmartAnalystContinueChatResponse,
  SmartAnalystCreateChatResponse,
  SmartAnalystGetAllChatsResponse,
  SmartAnalystGetChatResponse,
  SmartAnalystInitChatResponse,
} from 'api/resources/smartAnalyst/types';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';

export const initSmartAnalystChat = (
  accountId: string,
  projectId: string,
  effectiveDate: string,
  version: string = 'v1',
) => {
  return doPost<SmartAnalystInitChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_INIT_CHAT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { effective_date: effectiveDate },
    { prefix: 'nebula', version: version },
  );
};

export const createSmartAnalystChat = (
  accountId: string,
  projectId: string,
  userInput: string,
  version: string = 'v1',
) => {
  return doPost<SmartAnalystCreateChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHATS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { user_input: userInput },
    { prefix: 'nebula', version: version },
  );
};

export const createSmartAnalystChatReasoning = (
  accountId: string,
  projectId: string,
  userInput: string,
) => {
  return doPost<SmartAnalystCreateChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHATS_REASONING, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { user_input: userInput },
    { prefix: 'nebula', version: 'v2' },
  );
};

export const continueSmartAnalystChat = (
  accountId: string,
  projectId: string,
  chatId: string,
  userInput: string,
  version: string = 'v1',
) => {
  return doPost<SmartAnalystContinueChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT, {
      accountId,
      projectId,
      chatId,
    }),
    LOGGER_TYPE.JUPITER,
    { user_input: userInput },
    { prefix: 'nebula', version: version },
  );
};

export const continueSmartAnalystChatReasoning = (
  accountId: string,
  projectId: string,
  chatId: string,
  userInput: string,
) => {
  return doPost<SmartAnalystContinueChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT_REASONING, {
      accountId,
      projectId,
      chatId,
    }),
    LOGGER_TYPE.JUPITER,
    { user_input: userInput },
    { prefix: 'nebula', version: 'v2' },
  );
};

export const getSmartAnalystChats = (
  accountId: string,
  projectId: string,
  version: string = 'v1',
) => {
  return doGet<SmartAnalystGetAllChatsResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHATS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { prefix: 'nebula', version: version },
  );
};

export const getSmartAnalystChat = (
  accountId: string,
  projectId: string,
  chatId: string,
  version: string = 'v1',
) => {
  return doGet<SmartAnalystGetChatResponse>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT, {
      accountId,
      projectId,
      chatId,
    }),
    LOGGER_TYPE.JUPITER,
    { prefix: 'nebula', version: version },
  );
};

export const updateSmartAnalystChat = (
  accountId: string,
  projectId: string,
  chatId: string,
  title: string,
  version: string = 'v1',
) => {
  return doPatch<SmartAnalystChatMetadata>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT, {
      accountId,
      projectId,
      chatId,
    }),
    LOGGER_TYPE.JUPITER,
    { title },
    { prefix: 'nebula', version: version },
  );
};

export const deleteSmartAnalystChat = (
  accountId: string,
  projectId: string,
  chatId: string,
  version: string = 'v1',
) => {
  return doDelete<void>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT, {
      accountId,
      projectId,
      chatId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { prefix: 'nebula', version: version },
  );
};

export const reviewSmartAnalystChatMessage = (
  accountId: string,
  projectId: string,
  chatId: string,
  messageId: number,
  review: SmartAnalystChatMessageReview,
  comment?: string,
  version: string = 'v1',
) => {
  return doPost<void>(
    generateNextPath(ENDPOINTS.SMART_ANALYST_REVIEW_CHAT_MESSAGE, {
      accountId,
      projectId,
      chatId,
      messageId: messageId.toString(),
    }),
    LOGGER_TYPE.JUPITER,
    { review, comment },
    { prefix: 'nebula', version: version },
  );
};

export interface StreamSmartAnalystChatReturn {
  abort: () => void;
}

export const streamSmartAnalystChat = async (
  accountId: string,
  projectId: string,
  chatId: string,
  onMessage: (data: string) => void,
  onError?: (error: Error) => void,
): Promise<StreamSmartAnalystChatReturn> => {
  const abortController = new AbortController();

  const path = generateNextPath(ENDPOINTS.SMART_ANALYST_CHAT_STREAM, {
    accountId,
    projectId,
    chatId,
  });

  const url = buildApiUrl(path, 'nebula', 'v2');
  const headers = await buildApiHeaders({
    Accept: 'text/event-stream',
  });

  void (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'same-origin',
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `SSE request failed: ${response.status} ${response.statusText}`,
        );
      }

      await processSSEStream(response, onMessage);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      const errorInstance =
        error instanceof Error ? error : new Error(String(error));
      if (onError) {
        onError(errorInstance);
      } else {
        console.error('SSE Error:', errorInstance);
      }
    }
  })();

  return { abort: () => abortController.abort() };
};

export const getAnalysisTemplates = () => {
  return doGet<AnalysisTemplatesResponse>(
    ENDPOINTS.SMART_ANALYST_TEMPLATES,
    LOGGER_TYPE.JUPITER,
    { prefix: 'nebula' },
  );
};
